import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EDIT_URL, IMAGES, SHOW_HUE_EDIT } from "@/lib/api";
import { scrollToId } from "@/hooks/useLenis";

export default function Navigation({ visible, onContact }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -70, opacity: 0 }}
      animate={visible ? { y: 0, opacity: 1 } : { y: -70, opacity: 0 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: visible ? 0.25 : 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-700 ${
        scrolled ? "bg-[#F5F3EE]/85 backdrop-blur-md border-b border-[#e3ddd0]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1500px] mx-auto px-6 md:px-14 h-[76px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToId("#hero")}
          className="flex items-center"
          aria-label="Hue Creative home"
        >
          <img
            src={IMAGES.logo}
            alt="Hue Creative"
            className="h-8 w-auto transition-[filter] duration-500"
            style={scrolled ? undefined : { filter: "brightness(0) invert(1)" }}
          />
        </button>

        <div className="hidden md:flex items-center gap-12">
          {SHOW_HUE_EDIT && (
            <a
              data-testid="nav-the-edit"
              href={EDIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`link-underline text-[12px] tracking-[0.22em] uppercase transition-colors duration-500 ${
                scrolled ? "text-[#23221f]" : "text-[#F5F3EE]"
              }`}
            >
              The Edit
            </a>
          )}
          <button
            data-testid="nav-contact"
            onClick={onContact}
            className={`link-underline text-[12px] tracking-[0.22em] uppercase transition-colors duration-500 ${
              scrolled ? "text-[#23221f]" : "text-[#F5F3EE]"
            }`}
          >
            Contact
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden transition-colors duration-500 ${scrolled ? "text-[#23221f]" : "text-[#F5F3EE]"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#F5F3EE] border-b border-[#e3ddd0]"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {SHOW_HUE_EDIT && (
                <a
                  data-testid="nav-the-edit-mobile"
                  href={EDIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-display text-3xl font-light text-[#23221f]"
                >
                  The Edit
                </a>
              )}
              <button
                data-testid="nav-contact-mobile"
                onClick={() => {
                  setOpen(false);
                  onContact();
                }}
                className="text-left font-display text-3xl font-light text-[#23221f]"
              >
                Contact
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
